import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalHeader,
  Row,
} from "reactstrap"
import StartAssessment from "../StartAssessment"
import ViewAssessment from "../ViewAssessment"
import DeleteModal from "components/Common/DeleteModal"

const firstAttendanceInstructions = [
  "Attendance is mandatory throughout the entire training period and is a requirement for completing the program.",
  "Please arrive at the training location 15 minutes before the scheduled time to ensure a smooth check-in process.",
  "You must present the invitation SMS upon arrival at the training venue.",
  "A national ID or digital ID via the “Tawakkalna” app must be shown to complete the attendance process.",
  "Formal attire is required during all training days.",
]

const secondInstructions = [
  "Please maintain silence during the training sessions.",
  "Adhere to the instructions of the supervision and organizing team inside the training hall.",
  "The use of mobile phones or smart devices is prohibited except for training purposes.",
  "Please bring a smart device (mobile or tablet) to use for the electronic test.",
]

const thirdInstructions = [
  "The training curriculum is available on the theoretical training page within the electronic system.",
  "We recommend reviewing it in advance to enhance understanding and gain maximum benefit from the program.",
  "Any inquiries can be directed to the support team or the trainer.",
]

const Record = ({ name, value }) => {
  return (
    <Col xs="6" md="2" className="mb-2">
      <div className="font-bold">{name}</div>
      <div>{value}</div>
    </Col>
  )
}

const TheoreticalTrainingPreview = ({ training, getAllData }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const [modal1, setModal1] = useState(false)
  const [modalType, setModalType] = useState(null)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const toggle1 = () => setModal1(!modal1)

  const handleStartAssessment = () => {
    toggle1()
    setModalType("START_INTERVIEW")
    setShowConfirmationModal(false)
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xl="12">
            <h4 className="card-title mb-4">{t("My Theoretical Training")}</h4>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col className="text-end">
            <div className="d-flex gap-3 action_buttons align-items-end justify-content-end">
              {training?.can_submit_assessment && (
                <Button
                  color="primary"
                  className="fit_content"
                  onClick={() => {
                    // setShowConfirmationModal(true)
                    handleStartAssessment()
                  }}
                >
                  {t("Start Assessment")}
                </Button>
              )}
              {training?.assessment_details?.is_submitted && (
                <Button
                  color="primary"
                  className="fit_content"
                  onClick={() => {
                    toggle1()
                    setModalType("VIEW_INTERVIEW")
                  }}
                >
                  {t("View Assessment")}
                </Button>
              )}
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <h5 className="text-primary">{t("Theoretical Training")}</h5>
          </Col>
        </Row>

        <Card className="mb-4">
          <CardBody>
            <Row className="text-center">
              <Record name={t("Training Name")} value={training?.name} />
              <Record
                name={t("Building Name")}
                value={training?.building_name}
              />
              <Record
                name={t("Date")}
                value={`${training?.start_date} ~ ${training?.end_date}`}
              />
              <Record
                name={t("Time")}
                value={`${training?.start_time} ~ ${training?.end_time}`}
              />
              <Record
                name={t("Attended Days")}
                value={training?.assessment_details?.attended_days}
              />
              <Record
                name={t("Attended Dates")}
                value={training?.assessment_details?.attended_dates?.dates?.join(
                  ", "
                )}
              />
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h6 className="text-primary fw-bold mb-3">
              {t("Training Materials")}
            </h6>
            {training?.training_materials_details?.length > 0 &&
              training?.training_materials_details.map(tm => {
                return (
                  <div
                    key={tm.uuid}
                    onClick={() =>
                      window.open(
                        tm[language === "en" ? "contents" : "contents_ar"],
                        "_blank"
                      )
                    }
                    className="text-decoration-underline cursor-pointer"
                  >
                    {tm?.name}
                  </div>
                )
              })}
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h6 className="text-primary fw-bold mb-3">
              {t("Theoretical Training Attendance Instructions")}
            </h6>
            <p>{t("Location: Umm Al-Qura University – Building No. (36)")}</p>
            <p>{t("Location Link (Google Maps):")}</p>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://maps.app.goo.gl/pPffdWx7w4sw9UFL7"
            >
              https://maps.app.goo.gl/pPffdWx7w4sw9UFL7
            </a>

            <h6 className="fw-bold mt-4">
              {t("First: Attendance Instructions")}
            </h6>
            <ul>
              {firstAttendanceInstructions.map(item => {
                return <li key={item}>{t(item)}</li>
              })}
            </ul>

            <h6 className="fw-bold mt-4">
              {t("Second: Instructions Inside the Training Hall")}
            </h6>
            <ul>
              {secondInstructions.map(item => {
                return <li key={item}>{t(item)}</li>
              })}
            </ul>
            <h6 className="fw-bold mt-4">{t("Third: Training Material")}</h6>
            <ul>
              {thirdInstructions.map(item => {
                return <li key={item}>{t(item)}</li>
              })}
            </ul>

            <p>
              {t(
                "We wish you a rich and fruitful training experience, and we appreciate your adherence to the instructions to ensure the smooth operation of the program."
              )}
            </p>
          </CardBody>
        </Card>
      </Container>
      {showConfirmationModal && (
        <DeleteModal
          bodyTitle={t(
            "If you start the assessment now you cannot retake it again!"
          )}
          show={showConfirmationModal}
          buttonText={t("Start")}
          onCloseClick={() => setShowConfirmationModal(false)}
          onDeleteClick={handleStartAssessment}
          isLoading={false}
        />
      )}
      {modal1 && (
        <Modal
          isOpen={modal1}
          toggle={toggle1}
          className="full_screen_modal"
          backdrop="static"
        >
          <ModalHeader
            // toggle={modalType === "START_INTERVIEW" ? null : toggle1}
            toggle={toggle1}
            className={"d-flex justify-content-between"}
          >
            {modalType === "START_INTERVIEW"
              ? t("Start Assessment")
              : t("View Assessment Details")}
          </ModalHeader>
          {modalType === "START_INTERVIEW" && (
            <StartAssessment
              selectedItem={training}
              toggle1={toggle1}
              getAllData={getAllData}
            />
          )}
          {modalType === "VIEW_INTERVIEW" && (
            <ViewAssessment
              selectedItem={training}
              toggle1={toggle1}
              getAllData={getAllData}
            />
          )}
        </Modal>
      )}
    </div>
  )
}

export default TheoreticalTrainingPreview
